import { useEffect, useState } from "react";
import { Loader2, Activity, Search } from "lucide-react";

import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/auth-context";

const ACTION_OPTIONS = [
  "user_login",
  "user_register",
  "user_status_approved",
  "user_status_rejected",
  "user_status_suspended",
  "user_password_reset",
  "user_force_logout",
  "subscription_override",
  "case_view",
  "case_claim",
  "case_edit",
  "case_delete",
  "case_archive",
  "case_unarchive",
];

const ACTION_COLORS = {
  user_login: "bg-emerald-50 text-emerald-800",
  user_register: "bg-blue-50 text-blue-800",
  case_claim: "bg-[#C5A059]/20 text-[#0A192F]",
  case_delete: "bg-rose-50 text-rose-800",
  user_force_logout: "bg-amber-50 text-amber-900",
  user_password_reset: "bg-amber-50 text-amber-900",
};

function relTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `${Math.round(diff)}s ago`;
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return d.toLocaleString();
}

export default function AdminAudit() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState("all");
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (action !== "all") params.action = action;
      const { data } = await api.get("/admin/audit", { params });
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [action]);

  const filtered = items.filter((i) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return (
      i.actor_email?.toLowerCase().includes(s) ||
      i.target_id?.toLowerCase().includes(s) ||
      i.action?.toLowerCase().includes(s)
    );
  });

  return (
    <div data-testid="admin-audit" className="p-6 lg:p-10 max-w-7xl">
      <p className="text-xs tracking-[0.22em] uppercase font-semibold text-[#C5A059] mb-2">
        Admin
      </p>
      <h1 className="font-heading text-3xl lg:text-4xl font-semibold text-[#0A192F] flex items-center gap-3">
        <Activity className="w-7 h-7 text-[#C5A059]" /> Audit log
      </h1>
      <p className="mt-2 text-slate-600 text-sm">
        Every key platform event — logins, claims, admin actions — recorded.
      </p>

      <div className="mt-6 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            data-testid="audit-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search email, target, action…"
            className="pl-10 rounded-lg border-slate-300"
          />
        </div>
        <Select value={action} onValueChange={setAction}>
          <SelectTrigger data-testid="audit-action-filter" className="rounded-lg border-slate-300 sm:w-60">
            <SelectValue placeholder="Action" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All actions</SelectItem>
            {ACTION_OPTIONS.map((a) => (
              <SelectItem key={a} value={a}>{a}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-[#0A192F]" />
          </div>
        ) : filtered.length === 0 ? (
          <p data-testid="audit-empty" className="py-16 text-center text-slate-500">
            No audit events match.
          </p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {filtered.map((e) => (
              <li
                key={e.id}
                data-testid={`audit-row-${e.id}`}
                className="px-4 py-3 grid grid-cols-12 gap-3 items-center"
              >
                <span className="col-span-3 text-xs text-slate-500" title={e.created_at}>
                  {relTime(e.created_at)}
                </span>
                <span
                  className={`col-span-3 text-xs px-2 py-1 rounded-full font-semibold w-fit ${
                    ACTION_COLORS[e.action] || "bg-slate-100 text-slate-700"
                  }`}
                >
                  {e.action}
                </span>
                <span className="col-span-3 text-sm text-slate-700 truncate" title={e.actor_email}>
                  {e.actor_email || "system"}
                </span>
                <span className="col-span-3 text-xs text-slate-500 truncate" title={e.target_id}>
                  {e.target_type ? `${e.target_type}:` : ""}{e.target_id || ""}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
